import { useEffect, useState } from 'react'

import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'

import { useAPIPelaksanaanSchools } from 'api/kinerja/useAPIKinerja'
import { StepConfirmation } from './StepConfirmation'
import { StepSelectSchool } from './StepSelectSchool'
import { StepSuccess } from './StepSuccess'

import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'

type DialogPltKSConfirmationProps = Omit<DialogProps, 'children'>

type Step = 'SELECT_SCHOOL' | 'CONFIRMATION' | 'SUCCESS'

export function DialogPltKSConfirmation({
  isOpen,
  onClose,
  ...props
}: DialogPltKSConfirmationProps) {
  const [activeStep, setActiveStep] = useState<Step>('SELECT_SCHOOL')
  const [schools, setSchools] = useState<Array<PelaksanaanSchool>>([])

  const { data: dataSchools, isLoading: isLoadingSchools } =
    useAPIPelaksanaanSchools({
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  // Reset step upon mount
  useEffect(() => {
    setActiveStep('SELECT_SCHOOL')
  }, [isOpen])

  // Sync schools data with local state
  useEffect(() => {
    if (dataSchools) {
      // Only include the schools, if the role is PLT
      // and the status is either unconfirmed or rejected
      const schools =
        dataSchools.data
          ?.filter(
            (school) =>
              school.role === 'KS_PLT' &&
              (school.confirmationStatus === 'UNCONFIRMED' ||
                school.confirmationStatus === 'REJECTED')
          )
          // Use .map() to avoid mutation on the original object i.e. dataSchools.data
          // that might be used somewhere else via react-query cache
          .map(
            ({
              schoolName,
              npsn,
              role,
              confirmationStatus,
              perencanaanReadyState,
            }) => ({
              schoolName,
              npsn,
              role,
              confirmationStatus,
              perencanaanReadyState,
            })
          ) || []

      setSchools(schools)
    }
  }, [dataSchools])

  return (
    <Dialog
      size="sm"
      isOpen={isOpen}
      closeOnOverlayClick={false}
      onClose={onClose}
      {...props}
    >
      <DialogOverlay />
      <DialogContent className="p-6">
        {activeStep === 'SELECT_SCHOOL' && (
          <StepSelectSchool
            isLoadingSchools={isLoadingSchools}
            schools={schools}
            onChangeSelectedSchools={(schools) => setSchools(schools)}
            onNext={() => setActiveStep('CONFIRMATION')}
            onPrev={() => onClose?.()}
          />
        )}

        {activeStep === 'CONFIRMATION' && (
          <StepConfirmation
            schools={schools}
            onClose={() => onClose?.()}
            onPrev={() => setActiveStep('SELECT_SCHOOL')}
            onSuccess={() => setActiveStep('SUCCESS')}
          />
        )}

        {activeStep === 'SUCCESS' && (
          <StepSuccess onClose={() => onClose?.()} />
        )}
      </DialogContent>
    </Dialog>
  )
}
